import type { QuizAnswers } from "@/features/quiz"

import type { SoulmateInput } from "../types"

type Gender = SoulmateInput["gender"]
type PartnerGender = NonNullable<SoulmateInput["partnerGender"]>
type PartnerEthnicity = NonNullable<SoulmateInput["partnerEthnicity"]>

const GENDERS: Gender[] = ["male", "female", "other"]
const PARTNER_GENDERS: PartnerGender[] = ["male", "female", "any"]
const PARTNER_ETHNICITIES: PartnerEthnicity[] = ["african", "caucasian", "hispanic", "asian", "any"]

/** Extra quiz answers carried through as free-form colour for the LLM. */
const PREFERENCE_KEYS = [
  "partner-qualities",
  "looks-importance",
  "emotions-or-logic",
  "love-language",
  "partner-energy",
  "future-vision",
  "shared-experiences",
] as const

function asString(value: QuizAnswers[string] | undefined): string | null {
  return typeof value === "string" && value.trim() !== "" ? value.trim() : null
}

function inEnum<T extends string>(value: string | null, allowed: readonly T[]): T | null {
  return value && (allowed as readonly string[]).includes(value) ? (value as T) : null
}

/**
 * Build the public {@link SoulmateInput} snapshot from raw quiz answers. Works for
 * the soulmate quiz (partner fields present) and other quizzes (only `gender` +
 * `dob`). Returns null when the required `gender`/`dob` are missing or invalid —
 * the caller skips the report request then.
 */
export function buildSoulmateInput(answers: QuizAnswers): SoulmateInput | null {
  const gender = inEnum(asString(answers.gender), GENDERS)
  const dob = asString(answers.dob)
  if (!gender || !dob) return null

  const preferences: Record<string, string | string[]> = {}
  for (const key of PREFERENCE_KEYS) {
    const raw = answers[key]
    if (Array.isArray(raw) && raw.length > 0) preferences[key] = raw
    else {
      const str = asString(raw)
      if (str) preferences[key] = str
    }
  }

  return {
    gender,
    partnerGender: inEnum(asString(answers["partner-gender"]), PARTNER_GENDERS),
    partnerEthnicity: inEnum(asString(answers["partner-ethnicity"]), PARTNER_ETHNICITIES),
    partnerAge: asString(answers["partner-age"]),
    dob,
    preferences: Object.keys(preferences).length > 0 ? preferences : undefined,
  }
}
